import { type HueName, palette, paletteDark } from './primitives'

export type ThemeMode = 'light' | 'dark'

/** Neutral theo AntD: độ mờ trên nền, không phải mã xám cứng. */
const NEUTRAL = {
  light: {
    primary: '#000000E0',
    secondary: '#000000A6',
    disabled: '#00000040',
    tertiary: '#00000073',
    inverse: '#FFFFFFD9',
    borderSubtle: '#0000000F',
    borderDefault: '#00000026',
    borderStrong: '#00000040',
  },
  dark: {
    primary: '#FFFFFFD9',
    secondary: '#FFFFFFA6',
    disabled: '#FFFFFF40',
    tertiary: '#FFFFFF73',
    inverse: '#000000E0',
    borderSubtle: '#FFFFFF14',
    borderDefault: '#FFFFFF2E',
    borderStrong: '#FFFFFF40',
  },
} as const

const SURFACE = {
  light: {
    page: '#F2F6FC',
    surface: '#FFFFFF',
    sunken: '#F5F8FD',
    hover: '#0000000A',
    elevated: '#FFFFFF',
    overlay: '#00000073',
  },
  dark: {
    page: '#000000',
    surface: '#141414',
    sunken: '#1F1F1F',
    hover: '#FFFFFF14',
    elevated: '#1F1F1F',
    overlay: '#000000A6',
  },
} as const

export const buildSemantic = (mode: ThemeMode, hue: HueName) => {
  const p = mode === 'dark' ? paletteDark : palette
  const n = NEUTRAL[mode]
  const s = SURFACE[mode]

  return {
    text: {
      primary: n.primary,
      secondary: n.secondary,
      disabled: n.disabled,
      tertiary: n.tertiary,
      inverse: n.inverse,
      /** Chữ nằm TRÊN màu thương hiệu (nút primary, header bảng) — trắng ở cả hai chế độ,
       *  vì accent luôn là màu đậm bão hoà. Khác `inverse` (đảo theo nền trang). */
      onAccent: '#FFFFFF',
    },
    bg: {
      page: s.page,
      surface: s.surface,
      sunken: s.sunken,
      hover: s.hover,
      /** Popover / dropdown / thẻ nổi — sáng thì trắng, tối thì sáng hơn surface một bậc. */
      elevated: s.elevated,
      /** Lớp phủ mờ sau modal / ảnh (scrim). */
      overlay: s.overlay,
    },
    border: { subtle: n.borderSubtle, default: n.borderDefault, strong: n.borderStrong },
    accent: { base: p[hue][5], hover: p[hue][4], active: p[hue][6], bg: p[hue][0] },
    status: {
      success: p.success[5],
      warning: p.warning[5],
      error: p.error[5],
      info: p.info[5],
      successBg: p.success[0],
      warningBg: p.warning[0],
      errorBg: p.error[0],
      infoBg: p.info[0],
    },
    /**
     * Sơ đồ luồng ký (@xyflow/react + dagre). Không có nhóm này thì sơ đồ
     * nằm ngoài theme và vỡ ở dark mode.
     */
    diagram: {
      nodeBg: s.surface,
      nodeBorder: n.borderDefault,
      edge: n.secondary,
      stepDone: p.success[5],
      stepActive: p[hue][5],
      stepPending: n.disabled,
    },
  }
}

export type SemanticTokens = ReturnType<typeof buildSemantic>
